import { localized, msg, str } from '@lit/localize';
import hljs from 'highlight.js';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import '@spectrum-web-components/action-button/sp-action-button.js';
import '@spectrum-web-components/action-group/sp-action-group.js';
import '@spectrum-web-components/split-view/sp-split-view.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-chevron-down.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-chevron-up.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-copy.js';
import type { LoopIterationContext } from '../../../api/types';
import { emitToast } from '../../../components/toast-events';
import { sanitizeForDisplay } from '../utils/binary-display.js';
import { getLoopIterationDisplay } from '../utils/loop-iteration';
import './resizable-table.js';

export interface TaskDetailsBenchViewModel {
  id: number;
  name: string;
  label?: string | null;
  status?: string;
  error?: string | null;
  input?: Record<string, unknown>;
  output?: Record<string, unknown>;
  loopIteration?: LoopIterationContext | null;
  stateLoading?: boolean;
}

const DEFAULT_HEIGHT = 280;
const MIN_HEIGHT = 180;

@customElement('task-details-bench')
@localized()
export class TaskDetailsBench extends LitElement {
  static styles = css`
    :host {
      display: block;
      flex: 0 0 auto;
      min-width: 0;
      position: relative;
      z-index: 2;
    }
    .bench {
      box-sizing: border-box;
      background: linear-gradient(
        180deg,
        var(--color-sidebar-surface) 0%,
        var(--color-sidebar-bg) 100%
      );
      border-top: 1px solid var(--color-app-border);
      box-shadow: 0 -10px 28px rgba(15, 23, 42, 0.08);
      color: var(--color-app-text);
      display: flex;
      flex-direction: column;
      min-height: 0;
      overflow: hidden;
    }
    .bench.collapsed {
      min-height: 48px;
    }
    .resize-handle {
      align-items: center;
      cursor: ns-resize;
      display: flex;
      height: 10px;
      justify-content: center;
      left: 0;
      position: absolute;
      right: 0;
      top: 0;
      z-index: 3;
    }
    .resize-handle::before {
      background: var(--color-app-border);
      border-radius: 999px;
      content: '';
      display: block;
      height: 3px;
      opacity: 0.42;
      transition:
        opacity 120ms ease,
        width 120ms ease;
      width: 42px;
    }
    .resize-handle:hover::before,
    .resize-handle:focus-visible::before {
      background: var(--spectrum-gray-500);
      opacity: 0.76;
      width: 56px;
    }
    .bench-header {
      align-items: center;
      display: flex;
      gap: 12px;
      min-height: 48px;
      padding: 8px 14px;
    }
    .bench.expanded .bench-header {
      padding-top: 12px;
    }
    .title-block {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      gap: 3px;
      min-width: 0;
    }
    .title-row,
    .meta-row {
      align-items: center;
      display: flex;
      gap: 8px;
      min-width: 0;
    }
    .title {
      font-size: 14px;
      font-weight: 700;
      line-height: 1.25;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .label {
      color: var(--color-app-text-secondary);
      font-size: 12px;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .status-text {
      align-items: center;
      display: inline-flex;
      color: var(--color-sidebar-selected-border);
      font-size: 12px;
      font-weight: 650;
      gap: 6px;
      line-height: 1.2;
      white-space: nowrap;
    }
    .status-text::before {
      background: var(--color-sidebar-selected-border);
      border-radius: 999px;
      content: '';
      display: block;
      height: 7px;
      width: 7px;
    }
    .iteration-chip {
      align-items: center;
      border: 1px solid var(--color-app-border);
      border-radius: 999px;
      color: var(--color-app-text-secondary);
      display: inline-flex;
      flex: 0 0 auto;
      font-size: 11px;
      font-weight: 650;
      line-height: 1.2;
      padding: 3px 8px;
      white-space: nowrap;
    }
    .error-text {
      color: var(--color-error-text);
      font-size: 12px;
      font-weight: 600;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .bench-action {
      flex: 0 0 auto;
    }
    .bench-body {
      background: var(--color-sidebar-surface);
      border: 1px solid var(--color-app-border);
      border-radius: var(--radius-panel, 8px);
      box-sizing: border-box;
      display: flex;
      flex: 1 1 auto;
      margin: 12px 14px 14px;
      min-height: 0;
      overflow: hidden;
    }
    sp-split-view.io-split {
      background: var(--color-sidebar-surface);
      box-sizing: border-box;
      flex: 1 1 auto;
      height: 100%;
      min-height: 0;
      width: 100%;
      --mod-splitview-handle-background-color: var(--color-app-border);
      --mod-splitview-handle-background-color-hover: var(--spectrum-gray-500);
    }
    .pane {
      background: var(--color-sidebar-surface);
      box-sizing: border-box;
      min-height: 0;
      min-width: 0;
      overflow: hidden;
    }
    .section {
      background: var(--color-sidebar-surface);
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 0;
      overflow: hidden;
    }
    .section-header {
      align-items: center;
      background: var(--color-sidebar-bg);
      border-bottom: 1px solid var(--color-app-border);
      display: flex;
      gap: 8px;
      justify-content: space-between;
      min-height: 38px;
      padding: 6px 10px;
    }
    .section-title {
      font-size: 12px;
      font-weight: 700;
      margin: 0;
    }
    .section-actions {
      align-items: center;
      display: flex;
      gap: 6px;
    }
    .section-body {
      color: var(--color-app-text);
      flex: 1 1 auto;
      font-family: var(--mono);
      font-size: 12px;
      line-height: 1.45;
      min-height: 0;
      overflow: auto;
      padding: 10px;
      scrollbar-width: thin;
    }
    .section-body.empty {
      align-items: center;
      color: var(--color-app-text-secondary);
      display: flex;
      font-family: inherit;
      justify-content: center;
      text-align: center;
    }
    .section-body.table-body {
      font-family: var(--font);
      padding: 8px;
    }
    .section-body pre {
      background: transparent !important;
      cursor: text;
      margin: 0;
      padding: 0;
      user-select: text;
      white-space: pre-wrap;
      word-break: break-word;
    }
    .section-body pre code {
      background: transparent !important;
      font-family: inherit;
      font-size: inherit;
      padding: 0;
    }
    .section-body .hljs {
      background: transparent;
    }
    .section-body .hljs-attr {
      color: var(--color-syntax-attr);
    }
    .section-body .hljs-string {
      color: var(--color-syntax-string);
    }
    .section-body .hljs-number {
      color: var(--color-syntax-number);
    }
    .section-body .hljs-literal {
      color: var(--color-syntax-literal);
    }
    .section-body .hljs-punctuation {
      color: var(--color-syntax-punctuation);
    }
    .table-section {
      margin-bottom: 16px;
      min-width: 0;
    }
    .table-section:last-child {
      margin-bottom: 0;
    }
    .table-section-header {
      align-items: center;
      color: var(--color-app-text);
      display: flex;
      font-family: var(--font);
      font-size: 12px;
      font-weight: 700;
      gap: 12px;
      justify-content: space-between;
      margin: 0 0 8px;
    }
    .table-section-name {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .table-section-count {
      color: var(--color-app-text-secondary);
      flex: 0 0 auto;
      font-size: 11px;
      font-weight: 650;
    }
    @media (max-width: 760px) {
      .bench-body {
        margin: 10px;
      }
      .meta-row {
        flex-wrap: wrap;
      }
    }
  `;

  @property({ attribute: false }) accessor taskDetails:
    | TaskDetailsBenchViewModel
    | undefined = undefined;

  @property({ type: Boolean }) accessor expanded = false;

  @property({ type: Number }) accessor height = DEFAULT_HEIGHT;

  @state() private accessor viewMode: Record<string, 'json' | 'table'> = {};

  @state() private accessor isNarrow = false;

  private narrowQuery?: MediaQueryList;
  private resizeStartY = 0;
  private resizeStartHeight = DEFAULT_HEIGHT;

  connectedCallback(): void {
    super.connectedCallback();
    this.narrowQuery = window.matchMedia('(max-width: 760px)');
    this.isNarrow = this.narrowQuery.matches;
    this.narrowQuery.addEventListener('change', this.handleNarrowChange);
  }

  disconnectedCallback(): void {
    this.narrowQuery?.removeEventListener('change', this.handleNarrowChange);
    this.removeHeightResizeListeners();
    super.disconnectedCallback();
  }

  private handleNarrowChange = () => {
    this.isNarrow = this.narrowQuery?.matches ?? false;
  };

  private get clampedHeight() {
    return this.clampHeight(this.height);
  }

  private clampHeight(height: number) {
    const maxHeight = Math.max(220, Math.floor(window.innerHeight * 0.58));
    return Math.max(MIN_HEIGHT, Math.min(maxHeight, Math.round(height)));
  }

  private formatStatus(status?: string) {
    const normalized = status?.toString().trim().toLowerCase();
    if (!normalized) {
      return msg('Not run');
    }
    switch (normalized) {
      case 'pending':
        return msg('Pending');
      case 'running':
        return msg('Running');
      case 'completed':
        return msg('Completed');
      case 'failed':
        return msg('Failed');
      case 'success':
        return msg('Success');
      default:
        return status?.toString().trim() ?? '';
    }
  }

  private dispatchExpandedChange(expanded: boolean) {
    this.dispatchEvent(
      new CustomEvent('bench-expanded-change', {
        bubbles: true,
        composed: true,
        detail: { expanded },
      })
    );
  }

  private dispatchHeightChange(height: number) {
    this.dispatchEvent(
      new CustomEvent('bench-height-change', {
        bubbles: true,
        composed: true,
        detail: { height },
      })
    );
  }

  private toggleExpanded = () => {
    this.dispatchExpandedChange(!this.expanded);
  };

  private handleHeightResizeStart = (event: PointerEvent) => {
    event.preventDefault();
    event.stopPropagation();
    this.resizeStartY = event.clientY;
    this.resizeStartHeight = this.clampedHeight;
    window.addEventListener('pointermove', this.handleHeightResizeMove);
    window.addEventListener('pointerup', this.handleHeightResizeEnd, {
      once: true,
    });
  };

  private handleHeightResizeMove = (event: PointerEvent) => {
    const nextHeight = this.clampHeight(
      this.resizeStartHeight + (this.resizeStartY - event.clientY)
    );
    this.dispatchHeightChange(nextHeight);
  };

  private handleHeightResizeEnd = () => {
    this.removeHeightResizeListeners();
  };

  private removeHeightResizeListeners() {
    window.removeEventListener('pointermove', this.handleHeightResizeMove);
    window.removeEventListener('pointerup', this.handleHeightResizeEnd);
  }

  private async copyToClipboard(text: string) {
    try {
      if (!navigator?.clipboard?.writeText) {
        throw new Error('Clipboard API unavailable');
      }
      await navigator.clipboard.writeText(text);
      emitToast(this, {
        variant: 'success',
        message: msg('Copied to clipboard'),
      });
    } catch (err) {
      console.error('Failed to copy text:', err);
      emitToast(this, {
        variant: 'error',
        message: msg('Failed to copy'),
      });
    }
  }

  private escapeHtml(value: string) {
    return value.replace(
      /[&<>"']/g,
      (char) =>
        ({
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#039;',
        })[char] ?? char
    );
  }

  private highlightJson(jsonString: string): string {
    try {
      const highlighted = hljs.highlight(jsonString, {
        language: 'json',
      });
      return highlighted.value;
    } catch {
      return this.escapeHtml(jsonString);
    }
  }

  private getTableArrays(
    data: Record<string, unknown>
  ): Array<{ key: string; array: unknown[] }> {
    const tableArrays: Array<{ key: string; array: unknown[] }> = [];
    for (const [key, value] of Object.entries(data)) {
      if (!Array.isArray(value) || value.length === 0) {
        continue;
      }
      const firstItem = value[0];
      if (
        typeof firstItem === 'object' &&
        firstItem !== null &&
        !Array.isArray(firstItem)
      ) {
        tableArrays.push({ key, array: value });
      }
    }
    return tableArrays;
  }

  private renderTableView(items: unknown[], tableKey?: string) {
    if (!Array.isArray(items) || items.length === 0) {
      return html`<div class="section-body empty">${msg('No data to display')}</div>`;
    }
    const uniqueKey = tableKey
      ? `${tableKey}-${this.getTableKey(items)}`
      : this.getTableKey(items);

    return html`
      <resizable-table .data=${items} .tableKey=${uniqueKey}></resizable-table>
    `;
  }

  private formatRowCount(count: number) {
    return count === 1 ? msg('1 row') : msg(str`${count} rows`);
  }

  private getTableKey(items: unknown[]): string {
    if (Array.isArray(items) && items.length > 0) {
      const firstItem = items[0];
      if (typeof firstItem === 'object' && firstItem !== null) {
        const keys = Object.keys(firstItem as Record<string, unknown>).sort();
        return keys.join('|');
      }
    }
    return 'default';
  }

  private renderDataSection(
    title: string,
    data: Record<string, unknown> | undefined,
    emptyMessage: string,
    loading = false
  ) {
    if (loading) {
      return html`
        <section class="section" aria-label=${title}>
          <div class="section-header">
            <h3 class="section-title">${title}</h3>
          </div>
          <div class="section-body empty">${msg('Loading task state...')}</div>
        </section>
      `;
    }

    if (!data || Object.keys(data).length === 0) {
      return html`
        <section class="section" aria-label=${title}>
          <div class="section-header">
            <h3 class="section-title">${title}</h3>
          </div>
          <div class="section-body empty">${emptyMessage}</div>
        </section>
      `;
    }

    const displayData = sanitizeForDisplay(data) as Record<string, unknown>;
    let content = '';
    try {
      content = JSON.stringify(
        displayData,
        (_key, value) => (value === undefined ? null : value),
        2
      );
    } catch {
      return html`
        <section class="section" aria-label=${title}>
          <div class="section-header">
            <h3 class="section-title">${title}</h3>
          </div>
          <div class="section-body empty">${msg('Unable to display data.')}</div>
        </section>
      `;
    }

    const highlightedContent = this.highlightJson(content);
    const currentViewMode = this.viewMode[title] || 'json';
    const tableArrays = this.getTableArrays(displayData);
    const canShowTable = tableArrays.length > 0;
    const showTable = currentViewMode === 'table' && canShowTable;

    return html`
      <section class="section" aria-label=${title}>
        <div class="section-header">
          <h3 class="section-title">${title}</h3>
          <div class="section-actions">
            ${
              canShowTable
                ? html`
                    <sp-action-group
                      size="s"
                      compact
                      selects="single"
                      .selected=${
                        currentViewMode === 'table' ? ['table'] : ['json']
                      }
                      @change=${(event: Event) => {
                        const target = event.target as {
                          selected?: string[];
                        };
                        const selectedValue = target.selected?.[0] || 'json';
                        const selected: 'json' | 'table' =
                          selectedValue === 'table' ? 'table' : 'json';
                        this.viewMode = {
                          ...this.viewMode,
                          [title]: selected,
                        };
                      }}
                    >
                      <sp-action-button value="json">JSON</sp-action-button>
                      <sp-action-button value="table">${msg('Table')}</sp-action-button>
                    </sp-action-group>
                  `
                : nothing
            }
            <sp-action-button
              quiet
              size="s"
              class="copy-button"
              title=${msg(str`Copy ${title}`)}
              @click=${() => this.copyToClipboard(content)}
            >
              <sp-icon-copy slot="icon"></sp-icon-copy>
              ${msg('Copy')}
            </sp-action-button>
          </div>
        </div>
        <div class=${showTable ? 'section-body table-body' : 'section-body'}>
          ${
            showTable
              ? html`
                  ${tableArrays.map(
                    (tableData) => html`
                      <div class="table-section">
                        <h4 class="table-section-header">
                          <span class="table-section-name">
                            ${tableData.key}
                          </span>
                          <span class="table-section-count">
                            ${this.formatRowCount(tableData.array.length)}
                          </span>
                        </h4>
                        ${this.renderTableView(tableData.array, tableData.key)}
                      </div>
                    `
                  )}
                `
              : html`<pre class="hljs"><code>${unsafeHTML(
                  highlightedContent
                )}</code></pre>`
          }
        </div>
      </section>
    `;
  }

  render() {
    const taskDetails = this.taskDetails;
    if (!taskDetails) {
      return nothing;
    }

    const {
      name,
      label,
      status,
      error,
      input,
      output,
      loopIteration,
      stateLoading,
    } = taskDetails;
    const statusLabel = this.formatStatus(status);
    const hasError = !!error?.toString().trim();
    const heading = label ? `${name} (${label})` : name;
    const loopIterationDisplay = getLoopIterationDisplay(loopIteration);

    return html`
      <section
        class="bench ${this.expanded ? 'expanded' : 'collapsed'}"
        style=${this.expanded ? `height: ${this.clampedHeight}px` : ''}
        aria-label=${`Task details for ${heading}`}
      >
        ${
          this.expanded
            ? html`
                <div
                  class="resize-handle"
                  role="separator"
                  aria-orientation="horizontal"
                  tabindex="0"
                  @pointerdown=${this.handleHeightResizeStart}
                ></div>
              `
            : nothing
        }
        <header class="bench-header">
          <div class="title-block">
            <div class="title-row">
              <span class="title" title=${heading}>${name}</span>
              ${
                label
                  ? html`<span class="label" title=${label}>${label}</span>`
                  : nothing
              }
            </div>
            <div class="meta-row">
              ${
                hasError
                  ? html`<span class="error-text" title=${error ?? ''}
                      >${error}</span
                    >`
                  : html`<span class="status-text">${statusLabel}</span>`
              }
              ${
                loopIterationDisplay
                  ? html`
                      <span class="iteration-chip">
                        ${msg(
                          str`Iteration ${loopIterationDisplay.current} of ${loopIterationDisplay.total}`
                        )}
                      </span>
                    `
                  : nothing
              }
            </div>
          </div>
          <sp-action-button
            class="bench-action"
            quiet
            size="s"
            @click=${this.toggleExpanded}
            title=${
              this.expanded
                ? msg('Collapse task details')
                : msg('Open task details')
            }
          >
            ${
              this.expanded
                ? html`<sp-icon-chevron-down slot="icon"></sp-icon-chevron-down>`
                : html`<sp-icon-chevron-up slot="icon"></sp-icon-chevron-up>`
            }
            ${this.expanded ? msg('Collapse') : msg('Open')}
          </sp-action-button>
        </header>
        ${
          this.expanded
            ? html`
                <div class="bench-body">
                  <sp-split-view
                    class="io-split"
                    resizable
                    primary-size="50%"
                    .primaryMin=${260}
                    .secondaryMin=${260}
                    ?vertical=${this.isNarrow}
                    label=${msg('Resize task input and output details')}
                  >
                    <div class="pane">
                      ${this.renderDataSection(
                        msg('Input'),
                        input,
                        msg('No input data'),
                        stateLoading
                      )}
                    </div>
                    <div class="pane">
                      ${this.renderDataSection(
                        msg('Output'),
                        output,
                        msg('No output data'),
                        stateLoading
                      )}
                    </div>
                  </sp-split-view>
                </div>
              `
            : nothing
        }
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'task-details-bench': TaskDetailsBench;
  }
}
